import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import type { ThemePrimaryColor, ThemeFont } from "../api/types";

const DEFAULTS = {
  primary: "blue" as ThemePrimaryColor,
  font: "sansation" as ThemeFont,
};

// Marketing / pre-login routes that should render with the default theme
// EVEN IF an authenticated user somehow lands on them (e.g. an admin
// visiting the public terms page). NOTE: `"/"` is intentionally NOT here
// — `"/"` is conditional (RootGate): anonymous visitors get the landing
// page, which already renders with defaults via the `!user` branch below,
// while for authenticated users `"/"` is their DASHBOARD (HomePage →
// DashboardPage). Putting `"/"` here used to override the dashboard with
// the default "blue" accent on every load, masking the user's saved pick.
// `"/landing"` stays listed to cover its one redirect frame to `/`.
const PUBLIC_ROUTES = new Set([
  "/landing",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/invite",
  "/terms",
  "/privacy",
  "/acceptable-use",
  "/dpa",
  "/subprocessors",
  "/cookies",
]);

function isPublicPath(pathname: string): boolean {
  if (PUBLIC_ROUTES.has(pathname)) return true;
  if (pathname === "/landing/") return true;
  return false;
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Use the defaults only when (a) the route is an explicit marketing /
  // pre-login surface, OR (b) there's no authenticated user. An authed
  // user on an authed surface always gets THEIR accent, never a default.
  const useDefaults = isPublicPath(pathname) || !user;

  useEffect(() => {
    const root = document.documentElement;
    const primary = useDefaults ? DEFAULTS.primary : user?.theme_primary_color ?? DEFAULTS.primary;
    const font = useDefaults ? DEFAULTS.font : user?.theme_font ?? DEFAULTS.font;
    root.setAttribute("data-primary", primary);
    root.setAttribute("data-font", font);
  }, [
    useDefaults,
    user?.theme_primary_color,
    user?.theme_font,
  ]);

  return <>{children}</>;
}
